import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import http from 'app/requests/axiosInstance';
import {
  NotificationSetting,
  NotificationReminder,
  NotificationTemplate,
} from './notificationConstants';

// Reminders and templates have no backend yet, so they stay browser-local.
// Settings (the on/off switches) round-trip through the API below.
const STORAGE_KEY_REMINDERS = 'vara_notification_reminders';
const STORAGE_KEY_TEMPLATES = 'vara_notification_templates';

// The axios response interceptor deep-camelCases every payload, so the API's
// snake_case comes back camelCased while this screen's types stay snake_case.
interface ApiNotificationSetting {
  notificationKey: string;
  enabled: boolean;
  smsEnabled: boolean;
  emailEnabled: boolean;
  timingOffsetMinutes?: number | null;
}

const DEFAULT_SETTINGS: NotificationSetting[] = [
  {
    id: 's_1',
    notification_key: 'appointment_confirmation',
    category: 'customer_appointment',
    label: 'Appointment Confirmation',
    description: 'Send immediate confirmation when an appointment is booked.',
    enabled: true,
    sms_enabled: true,
    email_enabled: true,
    applicable_roles: ['admin', 'manager', 'staff'],
  },
  {
    id: 's_2',
    notification_key: 'appointment_reminder',
    category: 'customer_appointment',
    label: 'Appointment Reminders',
    description: 'Send automated reminders leading up to the appointment.',
    enabled: true,
    sms_enabled: true,
    email_enabled: true,
    applicable_roles: ['admin', 'manager', 'staff'],
  },
  {
    id: 's_3',
    notification_key: 'appointment_changed',
    category: 'customer_appointment',
    label: 'Appointment Rescheduled',
    description: 'Notify customer when appointment date or time is modified.',
    enabled: true,
    sms_enabled: true,
    email_enabled: true,
    applicable_roles: ['admin', 'manager', 'staff'],
  },
  {
    id: 's_4',
    notification_key: 'appointment_cancellation',
    category: 'customer_appointment',
    label: 'Appointment Cancellation',
    description: 'Notify customer when an appointment is cancelled.',
    enabled: true,
    sms_enabled: true,
    email_enabled: true,
    applicable_roles: ['admin', 'manager', 'staff'],
  },
  {
    id: 's_5',
    notification_key: 'post_appointment_thank_you',
    category: 'customer_followup',
    label: 'Post-Appointment Thank You',
    description: 'Send a thank you note shortly after the appointment completes.',
    enabled: true,
    sms_enabled: true,
    email_enabled: true,
    timing_offset_minutes: 120,
    applicable_roles: [],
  },
  {
    id: 's_6',
    notification_key: 'rebooking_reminder',
    category: 'customer_followup',
    label: 'Rebooking Reminder',
    description: 'Nudge customers who have not visited in a while to book again.',
    enabled: true,
    sms_enabled: false,
    email_enabled: true,
    timing_offset_minutes: 20160,
    applicable_roles: [],
  },
  {
    id: 's_7',
    notification_key: 'review_request',
    category: 'customer_followup',
    label: 'Review Request',
    description: 'Ask satisfied customers for an online review.',
    enabled: true,
    sms_enabled: true,
    email_enabled: false,
    timing_offset_minutes: 1440,
    applicable_roles: [],
    extra_config: {
      review_url: '',
    },
  },
  {
    id: 's_8',
    notification_key: 'staff_new_appointment',
    category: 'staff',
    label: 'New Appointment Booked',
    description: 'Alert team members when a new appointment is booked.',
    enabled: true,
    sms_enabled: true,
    email_enabled: true,
    applicable_roles: ['admin', 'manager', 'staff'],
  },
  {
    id: 's_9',
    notification_key: 'staff_appointment_rescheduled',
    category: 'staff',
    label: 'Appointment Rescheduled',
    description: 'Alert team members when an appointment time or details change.',
    enabled: true,
    sms_enabled: true,
    email_enabled: true,
    applicable_roles: ['admin', 'manager', 'staff'],
  },
  {
    id: 's_10',
    notification_key: 'staff_appointment_cancelled',
    category: 'staff',
    label: 'Appointment Cancelled',
    description: 'Alert team members when an appointment is cancelled.',
    enabled: true,
    sms_enabled: true,
    email_enabled: true,
    applicable_roles: ['admin', 'manager', 'staff'],
  },
];

const DEFAULT_REMINDERS: NotificationReminder[] = [
  {
    id: 'r_1',
    setting_id: 's_2',
    offset_minutes: 1440,
    sms_enabled: true,
    email_enabled: true,
  },
  {
    id: 'r_2',
    setting_id: 's_2',
    offset_minutes: 120,
    sms_enabled: true,
    email_enabled: false,
  },
  {
    id: 'r_3',
    setting_id: 's_7',
    offset_minutes: 20160,
    sms_enabled: true,
    email_enabled: true,
  },
];

const DEFAULT_TEMPLATES: NotificationTemplate[] = [
  {
    id: 't_1',
    template_key: 'appointment_confirmation_sms',
    label: 'Appointment Confirmation SMS',
    body: 'Hi {{customer_name}}, your appointment for {{service_name}} at {{business_name}} is confirmed for {{appointment_date}} at {{appointment_time}}.',
    type: 'sms',
  },
  {
    id: 't_2',
    template_key: 'appointment_confirmation_email',
    label: 'Appointment Confirmation Email',
    subject: 'Appointment Confirmed - {{business_name}}',
    body: 'Hi {{customer_name}},\n\nYour appointment for {{service_name}} is confirmed for {{appointment_date}} at {{appointment_time}} at {{location_address}}.\n\nWe look forward to seeing you!\n{{business_name}}',
    type: 'email',
  },
  {
    id: 't_3',
    template_key: 'appointment_reminder_sms',
    label: 'Appointment Reminder SMS',
    body: 'Reminder: Your appointment for {{service_name}} at {{business_name}} is on {{appointment_date}} at {{appointment_time}}.',
    type: 'sms',
  },
  {
    id: 't_4',
    template_key: 'appointment_reminder_email',
    label: 'Appointment Reminder Email',
    subject: 'Upcoming Appointment Reminder - {{business_name}}',
    body: 'Hi {{customer_name}},\n\nThis is a friendly reminder for your upcoming appointment for {{service_name}} on {{appointment_date}} at {{appointment_time}}.\n\nSee you soon,\n{{business_name}}',
    type: 'email',
  },
  {
    id: 't_5',
    template_key: 'appointment_changed_sms',
    label: 'Appointment Rescheduled SMS',
    body: 'Your appointment at {{business_name}} has been updated to {{appointment_date}} at {{appointment_time}}.',
    type: 'sms',
  },
  {
    id: 't_6',
    template_key: 'appointment_changed_email',
    label: 'Appointment Rescheduled Email',
    subject: 'Appointment Rescheduled - {{business_name}}',
    body: 'Hi {{customer_name}},\n\nYour appointment for {{service_name}} has been rescheduled to {{appointment_date}} at {{appointment_time}}.\n\n{{business_name}}',
    type: 'email',
  },
  {
    id: 't_7',
    template_key: 'appointment_cancellation_sms',
    label: 'Appointment Cancellation SMS',
    body: 'Your appointment for {{service_name}} on {{appointment_date}} at {{business_name}} has been cancelled.',
    type: 'sms',
  },
  {
    id: 't_8',
    template_key: 'appointment_cancellation_email',
    label: 'Appointment Cancellation Email',
    subject: 'Appointment Cancelled - {{business_name}}',
    body: 'Hi {{customer_name}},\n\nYour appointment for {{service_name}} on {{appointment_date}} at {{appointment_time}} has been cancelled.\n\n{{business_name}}',
    type: 'email',
  },
  {
    id: 't_9',
    template_key: 'post_thank_you_sms',
    label: 'Post-Appointment Thank You SMS',
    body: 'Thank you for visiting {{business_name}} today! We hope you loved your service.',
    type: 'sms',
  },
  {
    id: 't_10',
    template_key: 'post_appointment_thank_you_sms',
    label: 'Post-Appointment Thank You SMS',
    body: 'Thank you for visiting {{business_name}} today! We hope you loved your service.',
    type: 'sms',
  },
  {
    id: 't_11',
    template_key: 'review_request_sms',
    label: 'Review Request SMS',
    body: 'Hi {{customer_name}}, how was your recent visit to {{business_name}}? We would love your feedback: {{review_url}}',
    type: 'sms',
  },
  {
    id: 't_12',
    template_key: 'rebooking_reminder_sms',
    label: 'Rebooking Reminder SMS',
    body: "Hi {{customer_name}}, it's time for your next appointment at {{business_name}}! Book online: {{booking_link}}",
    type: 'sms',
  },
  {
    id: 't_13',
    template_key: 'staff_new_appointment_email',
    label: 'Staff New Appointment Email',
    subject: 'New Appointment: {{customer_name}} - {{service_name}}',
    body: 'New booking received:\n\nCustomer: {{customer_name}}\nService: {{service_name}}\nDate & Time: {{appointment_date}} at {{appointment_time}}\nStaff: {{staff_name}}',
    type: 'email',
  },
  {
    id: 't_14',
    template_key: 'staff_appointment_rescheduled_email',
    label: 'Staff Appointment Rescheduled Email',
    subject: 'Rescheduled: {{customer_name}} - {{service_name}}',
    body: 'Appointment rescheduled:\n\nCustomer: {{customer_name}}\nService: {{service_name}}\nNew Date & Time: {{appointment_date}} at {{appointment_time}}\nStaff: {{staff_name}}',
    type: 'email',
  },
  {
    id: 't_15',
    template_key: 'staff_appointment_cancelled_email',
    label: 'Staff Appointment Cancelled Email',
    subject: 'Cancelled: {{customer_name}} - {{service_name}}',
    body: 'Appointment cancelled:\n\nCustomer: {{customer_name}}\nService: {{service_name}}\nDate & Time: {{appointment_date}} at {{appointment_time}}\nStaff: {{staff_name}}',
    type: 'email',
  },
];

export const useNotificationData = () => {
  // DEFAULT_SETTINGS supplies the presentation (label, description, category,
  // timings, roles); the API only owns the three booleans and is merged over it.
  const [settings, setSettings] = useState<NotificationSetting[]>(DEFAULT_SETTINGS);

  const [reminders, setReminders] = useState<NotificationReminder[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_REMINDERS);
      return saved ? JSON.parse(saved) : DEFAULT_REMINDERS;
    } catch {
      return DEFAULT_REMINDERS;
    }
  });

  const [templates, setTemplates] = useState<NotificationTemplate[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_TEMPLATES);
      return saved ? JSON.parse(saved) : DEFAULT_TEMPLATES;
    } catch {
      return DEFAULT_TEMPLATES;
    }
  });

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await http.get('/v1/notification_settings');
        const saved: ApiNotificationSetting[] = data?.notificationSettings ?? [];
        if (cancelled) return;

        const byKey = new Map(saved.map((row) => [row.notificationKey, row]));
        setSettings((prev) =>
          prev.map((setting) => {
            const row = byKey.get(setting.notification_key);
            return row
              ? {
                  ...setting,
                  enabled: row.enabled,
                  sms_enabled: row.smsEnabled,
                  email_enabled: row.emailEnabled,
                  timing_offset_minutes: row.timingOffsetMinutes ?? setting.timing_offset_minutes,
                }
              : setting;
          })
        );
      } catch (err: any) {
        if (cancelled) return;
        const msg = err?.data?.error || 'Failed to load notification settings';
        setError(msg);
        toast.error(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateSetting = (id: string, updates: Partial<NotificationSetting>) => {
    setSettings((prev) =>
      prev.map((setting) => (setting.id === id ? { ...setting, ...updates } : setting))
    );
  };

  const updateTemplate = (id: string, updates: Partial<NotificationTemplate>) => {
    setTemplates((prev) =>
      prev.map((tmpl) => (tmpl.id === id ? { ...tmpl, ...updates } : tmpl))
    );
  };

  const addReminder = (settingId: string, defaultOffsetMinutes: number = 1440) => {
    const newReminder: NotificationReminder = {
      id: `r_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      setting_id: settingId,
      offset_minutes: defaultOffsetMinutes,
      sms_enabled: true,
      email_enabled: true,
    };
    setReminders((prev) => [...prev, newReminder]);
  };

  const removeReminder = (id: string) => {
    setReminders((prev) => prev.filter((r) => r.id !== id));
  };

  const updateReminder = (id: string, updates: Partial<NotificationReminder>) => {
    setReminders((prev) =>
      prev.map((reminder) => (reminder.id === id ? { ...reminder, ...updates } : reminder))
    );
  };

  const saveAll = async () => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await http.put('/v1/notification_settings', {
        notification_settings: settings.map((s) => ({
          notification_key: s.notification_key,
          enabled: s.enabled,
          sms_enabled: s.sms_enabled,
          email_enabled: s.email_enabled,
          // Only the follow-ups carry a timing; undefined is dropped from the JSON.
          timing_offset_minutes: s.timing_offset_minutes,
        })),
      });

      const saved: ApiNotificationSetting[] = data?.notificationSettings ?? [];
      const byKey = new Map(saved.map((row) => [row.notificationKey, row]));
      setSettings((prev) =>
        prev.map((setting) => {
          const row = byKey.get(setting.notification_key);
          return row
            ? {
                ...setting,
                enabled: row.enabled,
                sms_enabled: row.smsEnabled,
                email_enabled: row.emailEnabled,
                timing_offset_minutes: row.timingOffsetMinutes ?? setting.timing_offset_minutes,
              }
            : setting;
        })
      );

      // No backend for these yet — keep them browser-local so the controls
      // at least survive a reload.
      localStorage.setItem(STORAGE_KEY_REMINDERS, JSON.stringify(reminders));
      localStorage.setItem(STORAGE_KEY_TEMPLATES, JSON.stringify(templates));

      toast.success('Notification settings saved successfully');
    } catch (err: any) {
      const msg = err?.data?.error || 'Failed to save notification settings';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return {
    settings,
    reminders,
    templates,
    loading,
    saving,
    error,
    updateSetting,
    updateTemplate,
    addReminder,
    removeReminder,
    updateReminder,
    saveAll,
  };
};
